import React from 'react';
import { Input } from './ui/input';

// ColorHexInput — holds local draft state so users can type a full hex
// without the field snapping back on each keystroke.
// Only calls onChange when a valid 3- or 6-digit hex (#xxx / #xxxxxx) is
// entered, and resets to the parent value on blur if the draft is invalid.
export const ColorHexInput = React.memo(function ColorHexInput({ value, onChange, placeholder, className, ...rest }) {
  const [draft, setDraft] = React.useState(value ?? '');
  const lastCommitted = React.useRef(value ?? '');

  React.useEffect(() => {
    const v = value ?? '';
    if (v !== lastCommitted.current) {
      lastCommitted.current = v;
      setDraft(v);
    }
  }, [value]);

  const isValidHex = (v) => /^#[0-9a-fA-F]{3}$/.test(v) || /^#[0-9a-fA-F]{6}$/.test(v);

  const handleChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (isValidHex(v)) {
      lastCommitted.current = v;
      onChange(e);
    }
  };

  const handleBlur = (e) => {
    if (isValidHex(draft)) {
      lastCommitted.current = draft;
      onChange({ ...e, target: { ...e.target, value: draft } });
    } else {
      const reset = value ?? '';
      setDraft(reset);
      lastCommitted.current = reset;
    }
  };

  return (
    <Input
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
});

// ColorPickerSwatch — a styled swatch that opens the native OS color picker
// on click. Uses a transparent overlay <input type="color"> so the browser's
// built-in picker (with HEX/RGB/CMYK cycling on macOS) is used. Emits
// onChange({ target: { value: '#xxxxxx' } }) to match native input API.
// onPickerMouseDown — optional callback fired before the picker opens (e.g.
//   to save a rich-text editor selection in TipTapEditor).
const ColorPickerSwatch = React.memo(function ColorPickerSwatch({
  value,
  onChange,
  className = '',
  'data-testid': testId,
  title,
  defaultValue = '#000000',
  onPickerMouseDown,
}) {
  const safeValue = React.useMemo(() => {
    const v = value || defaultValue;
    return /^#[0-9a-fA-F]{3,6}$/.test(v) ? v : defaultValue;
  }, [value, defaultValue]);

  return (
    <label
      className="relative inline-flex flex-shrink-0 cursor-pointer"
      data-testid={testId}
      title={title}
      style={{ display: 'inline-flex' }}
      onMouseDown={onPickerMouseDown}
    >
      {/* Visual coloured swatch */}
      <span
        className="w-8 h-8 rounded-md border-2 border-slate-300 hover:border-indigo-400 transition-all shadow-sm block pointer-events-none"
        style={{ backgroundColor: safeValue }}
      />
      {/* Native color input overlaid — opens the OS / Chrome colour picker */}
      <input
        type="color"
        value={safeValue}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        tabIndex={-1}
        aria-label="Pick colour"
      />
    </label>
  );
});

export default ColorPickerSwatch;
